import { observer } from "mobx-react-lite";
import { ZoneType } from "src/types/Zone";
import { LockClosedIcon } from "@heroicons/react/20/solid";
import { LockOpenIcon } from "@heroicons/react/20/solid";
import classNames from "classnames";
import { useEvent } from "src/hooks/useEvent";
import { useLayoutEffect, useRef, useState } from "react";
import { InspectorGroup } from "./InspectorGroup";
import { ZoneTypeSelect, ZoneComplianceMetrics } from "./ZoneControls";
import type { Selection } from "src/types/Selection";
import { IconAlertTriangleFilled, IconEdit } from "@tabler/icons-react";
import type { Zone } from "src/types/Zone";
import { autorun } from "mobx";
import type { World } from "src/types/World";
import { GetZoneStyle } from "../ZONE_POLYGON_STYLE";
import { useTranslation } from "src/translations";
import { UnitType } from "src/types/UnitType";

export const ZoneInspector = observer(function ZoneInspector(props: {
  selection: Selection;
  world: World | null;
  readonly: boolean;
  showWarning: boolean;
  unit: UnitType;
  onUpdate?: (zone: Zone) => void;
}) {
  const zones = props.selection.zones.length ? props.selection.zones : (props.world?.zones ?? []);
  const world = props.world;
  const { t } = useTranslation();
  return (
    <InspectorGroup label={t("zones.title")}>
      {world &&
        zones.map((zone) => (
          <ZoneSettings
            showWarning={props.showWarning}
            key={zone.id}
            zone={zone}
            readonly={props.readonly}
            world={world}
            unit={props.unit}
            onUpdate={props.onUpdate}
          />
        ))}
    </InspectorGroup>
  );
});

const ZoneSettings = observer(function ZoneSettings(props: {
  world: World;
  zone: Zone;
  readonly: boolean;
  showWarning: boolean;
  unit: UnitType;
  onUpdate?: (zone: Zone) => void;
}) {
  const { t } = useTranslation();
  const onZoneTypeChange = useEvent((type: ZoneType) => {
    props.zone.setZoneType(type);
    props.onUpdate?.(props.zone);
  });
  const onLockChange = useEvent((locked: boolean) => {
    props.zone.setLocked(locked);
    props.onUpdate?.(props.zone);
  });
  return (
    <div className="flex flex-col border-b border-neutral-4 items-center flex-1 space-y-2 py-[10px]">
      <div className="flex flex-row flex-1 w-full items-center">
        <ZonePreview zone={props.zone} enableWarnings={props.showWarning} />
        <div className="flex flex-row flex-1 items-center group">
          <Label text={props.zone.label} onChange={props.zone.setLabel} readonly={props.readonly} />
          {props.zone.zoneType === ZoneType.buildable && (
            <LockButton locked={props.zone.locked} onChange={onLockChange} />
          )}
        </div>
      </div>
      <div className="flex flex-row justify-between flex-1 w-full items-center">
        <div className="space-x-1 text-body-1 text-neutral-6">
          <span>{t("zones.area")}</span>
          <span className="text-neutral-8">
            {(props.unit === UnitType.Metric
              ? props.zone.siteAreaHectares
              : props.zone.siteAreaHectares * 2.471054
            ).toFixed(2)}
          </span>
          <span>{props.unit === UnitType.Metric ? t("units.ha") : t("units.ac")}</span>
        </div>
        <ZoneTypeSelect
          zone={props.zone}
          readonly={props.zone.locked || props.readonly}
          onChange={onZoneTypeChange}
        />
      </div>
      <ZoneComplianceMetrics zone={props.zone} />
      <NoBuildingWarning zone={props.zone} showWarning={props.showWarning} />
    </div>
  );
});

const NoBuildingWarning = observer(function NoBuildingWarning(props: {
  zone: Zone;
  showWarning: boolean;
}) {
  const { t } = useTranslation();
  if (!props.showWarning) return null;
  if (props.zone.zoneType !== ZoneType.buildable) return null;
  if (props.zone.hasBuilding) return null;
  return (
    <div className="bg-warning-2 p-2 rounded-sm flex w-full flex-col space-y-[2px]">
      <div className="flex space-x-1 ">
        <IconAlertTriangleFilled className="w-4 text-warning-6" />
        <span className="text-warning-8 text-subtitle-2">{t("zones.noBuildings")}</span>
      </div>
      <div className="cursor-pointer pl-5 text-body-1 text-warning-8">{t("zones.tooSmall")}</div>
    </div>
  );
});

const Label = observer(function Label(props: {
  text: string;
  onChange: (text: string) => void;
  readonly: boolean;
}) {
  const [draft, setDraft] = useState<null | string>(null);
  const onSubmit = useEvent(() => {
    if (!draft) return;
    setDraft(null);
    if (props.text === draft) return;
    props.onChange(draft);
  });
  return (
    <div className="flex flex-row flex-1">
      {draft === null && (
        <div className="text-neutral-7 text-subtitle-2 flex-1 truncate flex flex-row">
          {props.text}
        </div>
      )}
      {draft !== null && (
        <input
          autoFocus
          className="px-1"
          value={draft}
          onInput={(evt) => setDraft(evt.currentTarget.value)}
          onBlur={onSubmit}
          onKeyDown={(evt) => {
            if (evt.key === "Enter") {
              evt.preventDefault();
              onSubmit();
            } else if (evt.key === "Escape") {
              evt.preventDefault();
              setDraft(null);
            }
          }}
        />
      )}
      {draft === null && (
        <button
          disabled={props.readonly}
          className={classNames(
            "text-neutral-5 rounded-sm box-border hidden justify-center items-center px-1",
            !props.readonly && "hover:text-neutral-6 cursor-pointer group-hover:flex"
          )}
          onClick={props.readonly ? undefined : () => setDraft(props.text)}
        >
          <IconEdit className="w-4 h-4" />
        </button>
      )}
    </div>
  );
});

const LockButton = observer(function LockButton(props: {
  locked: boolean;
  onChange: (locked: boolean) => void;
}) {
  const lock = props.locked ? <LockClosedIcon /> : <LockOpenIcon />;
  return (
    <button
      onClick={() => props.onChange(!props.locked)}
      className={classNames(
        "w-6 h-6 p-[6px] border box-border rounded-sm border-neutral-5 ml-2",
        !props.locked && "bg-neutral-1 text-neutral-6",
        props.locked && "bg-primary-6 text-white"
      )}
    >
      {lock}
    </button>
  );
});

const ZonePreview = observer(function ZonePreview(props: { zone: Zone; enableWarnings: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useLayoutEffect(() => {
    return autorun(() => {
      const style = GetZoneStyle(props.zone, props.enableWarnings);
      const ctx = ref.current?.getContext("2d");
      if (!ctx) return;
      const { height, left, top, width } = props.zone.bounds;
      const padding = 10;
      const scaleSize = Math.max(width, height);
      // scale the polygon to fit the canvas
      const points = props.zone.outerRing.map((p) => {
        const px = p[0];
        const py = p[2];
        const x = ((px - left) / scaleSize) * (ctx.canvas.width - padding);
        const y = ((py - top) / scaleSize) * (ctx.canvas.height - padding);
        return [x, y];
      });
      // center the polygon on canvas
      const minX = Math.min(...points.map((p) => p[0]));
      const maxX = Math.max(...points.map((p) => p[0]));
      const minY = Math.min(...points.map((p) => p[1]));
      const maxY = Math.max(...points.map((p) => p[1]));
      const offsetX = (ctx.canvas.width - (maxX - minX)) / 2;
      const offsetY = (ctx.canvas.height - (maxY - minY)) / 2;
      points.forEach((p) => {
        p[0] += offsetX;
        p[1] += offsetY;
      });

      ctx.imageSmoothingEnabled = true;
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      ctx.fillStyle = formatHexColor(style.NONE.FILL_COLOR);
      ctx.beginPath();
      ctx.strokeStyle = formatHexColor(style.NONE.BORDER_COLOR);
      ctx.lineWidth = 1;
      points.forEach(([x, y], i) => {
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
      ctx.closePath();
      ctx.fill();
    });
  }, [props.enableWarnings, props.zone]);
  const style = { width: 32, height: 32 };
  return (
    <div style={style} className="border border-neutral-4 mr-[6px] box-border rounded-sm">
      <canvas
        ref={ref}
        style={{ ...style, transform: "rotate(0deg)" }}
        width={style.width}
        height={style.height}
      />
    </div>
  );
});

function formatHexColor(hexColorValue: number | string): string {
  if (typeof hexColorValue === "string") return hexColorValue;
  const hexColor = hexColorValue.toString(16); // Convert to hexadecimal string
  const paddedHexColor = "#" + "0".repeat(6 - hexColor.length) + hexColor; // Pad with zeros if needed

  return paddedHexColor;
}
